export interface WarningSign { t: string; d: string }
export interface ProcessStep { n: string; t: string; d: string }

export interface Service {
  slug: string;
  name: string;
  short: string;
  title: string;
  description: string;
  icon: string;
  intro: string;
  intro2?: string;
  whatWeFix: string[];
  priceRange: string;
  // Unique per-service blocks. `null` for warningSigns/processSteps = skip the block entirely.
  warningSigns?: WarningSign[] | null;
  pricingNote?: string;
  pricingBullets?: string[];
  processNote?: string;
  processSteps?: ProcessStep[] | null;
  faqs: { q: string; a: string }[];
  citySlug?: string;
  // Optional H1 override — used instead of the auto-generated "{name} in {city}, AZ" heading.
  h1?: string;
}

// Reusable default process steps when a service doesn't define its own (avoids
// the old "$150 faucet swap or a $12,000 repipe" boilerplate). Each service
// below either inherits this honest version or overrides with its own steps.
const defaultProcess: ProcessStep[] = [
  { n: "01", t: "Live call or quote request", d: "A real Phoenix dispatcher answers and books an arrival window — no phone trees." },
  { n: "02", t: "Tech arrives & diagnoses", d: "A licensed plumber inspects the system, not just the symptom, and explains what's going on." },
  { n: "03", t: "Flat-rate written quote", d: "We hand you one all-in price before any work begins. You approve it before we touch a wrench." },
  { n: "04", t: "Work, walkthrough, warranty", d: "We complete the job, walk you through what was done, clean up, and back it in writing." },
];

export const SERVICES: Service[] = [
  {
    slug: "emergency-plumbing-phoenix",
    name: "Emergency Plumbing",
    short: "Burst pipes, floods, sewer backups, gas leaks — answered.",
    title: "24/7 Emergency Plumber Phoenix | Fast Response, Day or Night",
    description:
      "Burst pipe? Flood? Sewer backup? Phoenix Plumbing Co. answers around the clock across the Valley. Upfront pricing, written warranty. Call 602-834-1208 now.",
    icon: "Siren",
    intro:
      "When water is pouring through your ceiling or sewage is backing up into the tub, every minute matters. Phoenix Plumbing Co. answers emergency calls during business hours and arrives at most Valley homes within 60 to 90 minutes with a fully stocked truck and a licensed plumber.",
    intro2:
      "Every emergency call starts with a phone triage so you can shut off the right valve before we arrive — that alone often saves thousands in drywall and flooring. Once on site we stop the active damage first, then walk you through the repair options and a flat price.",
    whatWeFix: [
      "Burst, frozen, or split pipes",
      "Sewer line backups and overflowing toilets",
      "Water heater failures and leaks",
      "Gas leaks and gas line ruptures",
      "Slab leaks and hidden pinhole leaks",
      "No-water and low-pressure emergencies",
    ],
    priceRange: "Emergency diagnostic $89 • Most emergency repairs $200–$900",
    warningSigns: [
      { t: "Water actively running where it shouldn't", d: "Behind a wall, under a sink, through the ceiling — shut off the main and call immediately." },
      { t: "Sewage backing up into a tub or floor drain", d: "A main-line clog or break. Stop running every fixture in the house until we clear it." },
      { t: "Smell of gas anywhere in or outside the home", d: "Leave the building, leave doors open, don't flip a switch, and call from a safe distance." },
      { t: "No water at any fixture", d: "Usually a failed PRV, main shutoff, or supply break. We carry replacement parts on the truck." },
    ],
    pricingNote: "Emergency calls carry a small after-hours dispatch fee, but our per-hour labor rate stays the same — and we still quote a flat price for the full repair before any work begins.",
    pricingBullets: [
      "Flat-rate emergency quote — no surprise hourly billing",
      "Diagnostic fee credited toward the repair",
      "Parts stocked on-truck for the most common failures",
    ],
    processNote: "An emergency call moves faster than a scheduled visit, but it still follows the same four steps so you never get blindsided on price.",
    processSteps: [
      { n: "01", t: "Phone triage", d: "We tell you which valve to shut off so damage stops while we drive." },
      { n: "02", t: "On-site stabilization", d: "We stop the active leak or flood first — repair planning second." },
      { n: "03", t: "Flat emergency quote", d: "Written price before any repair work begins. You approve it before we go further." },
      { n: "04", t: "Repair & warranty", d: "Workmanship guarantee in writing on every emergency repair." },
    ],
    faqs: [
      { q: "How fast can you get to my Phoenix home?", a: "Most calls inside the 101 loop see a licensed plumber within 60–90 minutes. Outer cities like Surprise or Anthem typically 90–120 minutes." },
      { q: "Do you charge extra after hours?", a: "There is a modest after-hours dispatch fee, but our labor rate doesn't change and the full repair is still quoted flat before any work begins." },
      { q: "What counts as a plumbing emergency?", a: "Burst pipes, active flooding, sewage backup, gas leaks, no hot water in winter, and water heater failures all qualify. When in doubt, call — we'll triage on the phone." },
      { q: "Should I shut anything off before you arrive?", a: "Yes. Turn off the main water valve (usually at the front-yard side of the house) and, for gas leaks, the gas meter shutoff. Leave the building if you smell gas." },
      { q: "Will you talk me through stopping damage while you drive?", a: "Always. The dispatcher stays on the phone with you to find your shutoff and minimize damage until the plumber arrives." },
    ],
  },
  {
    slug: "residential-plumbing-phoenix",
    name: "Residential Plumbing",
    short: "Repairs, installs, and remodels for Phoenix homes of every age.",
    title: "Residential Plumbing Phoenix | Homes, Condos & Rentals",
    description:
      "Residential plumbers serving Phoenix homes since 2012. Repairs, water heaters, drains, leaks & remodels. Licensed, bonded & insured. Call 602-834-1208.",
    h1: "Residential Plumbing in Phoenix — Homes, Condos & Rental Properties",
    icon: "Home",
    intro:
      "From 1950s ranch homes in Arcadia to new builds in Desert Ridge, Phoenix Plumbing Co. handles every residential plumbing job in the Valley. Upfront pricing, licensed technicians, and a workmanship guarantee on every job.",
    intro2:
      "We work on homes built in every era Phoenix has — galvanized in mid-century neighborhoods, polybutylene in the late 70s and 80s, copper through the 90s, and PEX today. The age of the house decides the approach, and we tell you which one you've got before we quote.",
    whatWeFix: [
      "Faucets, toilets, sinks, showers, tubs",
      "Water heaters (tank & tankless)",
      "Drain clogs and slow drains",
      "Slab leaks and pipe repairs",
      "Whole-home repipes (PEX or copper)",
      "Water softeners and filtration",
    ],
    priceRange: "Service call $89 • Common repairs $150–$500",
    warningSigns: [
      { t: "Water bill creeping up with no usage change", d: "Often a silent toilet leak or hidden supply line drip — easy to find, cheap to fix early." },
      { t: "Low pressure at one fixture only", d: "Usually a clogged aerator or cartridge, not a whole-house problem." },
      { t: "Discolored water for more than a minute", d: "Either a failing water heater or a corroded supply line. We can test both in one visit." },
      { t: "Multiple drains slow at once", d: "Points at a main-line restriction, not individual clogs. Snake or jet decision happens on camera." },
    ],
    pricingNote: "Residential work is quoted flat by repair, not by the hour, so a 20-minute fix doesn't get padded into a half-day bill. The diagnostic fee comes off the repair if you proceed.",
    pricingBullets: [
      "Flat-rate quote per repair — sign before we start",
      "Diagnostic fee credited toward the work",
      "Senior, military, and first-responder discounts",
      "Financing available on installs over $1,000",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "Do you offer free estimates?", a: "Yes — estimates on installs and replacements are free. We charge a small diagnostic fee for repairs that is credited toward the work if you proceed." },
      { q: "Are you licensed and insured?", a: "Yes. We are a licensed, bonded, and insured AZ ROC contractor and carry $2M general liability." },
      { q: "Do you warranty your work?", a: "Every repair and install is backed by a written workmanship guarantee handed to you on the invoice." },
      { q: "Will the same plumber handle my house every time?", a: "Whenever scheduling allows, yes. We keep notes on every home so the next visit picks up where the last one left off." },
    ],
  },
  {
    slug: "water-heater-installation-and-repair",
    name: "Water Heater Repair & Installation",
    short: "Tank, tankless, and hybrid — same-day service across Phoenix.",
    title: "Water Heater Repair & Installation Phoenix AZ | Same-Day",
    description:
      "Tank, tankless, and hybrid — same-day service across Phoenix. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "Flame",
    intro:
      "Phoenix's hard water cuts the typical water heater lifespan to 8–12 years. Whether yours is leaking, cold, or just past its prime, we repair and install every major brand of tank, tankless, and hybrid heater — usually same-day on tank swaps.",
    intro2:
      "We stock 40, 50, and 75-gallon tanks (gas and electric) on the truck along with the most common tankless brands, so a same-day swap is realistic on most calls. Tankless installs and gas-line upgrades get scheduled within a few days.",
    whatWeFix: [
      "Leaking or rusted tanks",
      "No hot water / lukewarm water",
      "Pilot light & thermocouple issues",
      "Tankless descaling and repair",
      "Full tank and tankless installations",
      "Expansion tanks & recirculation pumps",
    ],
    priceRange: "Repairs $150–$600 • Tank replacement $1,500–$2,800 • Tankless $3,200–$5,500",
    warningSigns: [
      { t: "No hot water or runs out in minutes", d: "Failed heating element, bad thermostat, or sediment-choked tank — all diagnosable in one visit." },
      { t: "Popping or rumbling from the tank", d: "Hard-water sediment cooking on the burner. Past a certain point, flush won't save it." },
      { t: "Rusty or metallic-tasting hot water", d: "The tank's interior is corroding. Once the rust shows in the hot tap, the tank is on borrowed time." },
      { t: "Water pooling around the base", d: "Almost always a cracked tank, not a fitting. Replacement, not repair." },
      { t: "Tank is 10+ years old", d: "Phoenix hard water shortens lifespan to 8–12 years. Plan ahead before it fails on a Sunday." },
    ],
    pricingNote: "Water heater pricing depends on tank size, fuel type, code upgrades (expansion tank, drain pan, seismic strap), and whether we're swapping like-for-like or moving the unit. We quote all of that flat before any wrenches turn.",
    pricingBullets: [
      "Flat price covers heater, install, haul-away, and permit when required",
      "Brand options shown side by side with warranty terms",
      "Financing available on installs over $1,000",
    ],
    processNote: "Most standard tank-for-tank swaps are completed in 2–3 hours on the same day you call. Tankless conversions take a half to full day and often need a gas line or venting upgrade.",
    processSteps: defaultProcess,
    faqs: [
      { q: "How long does a water heater last in Phoenix?", a: "Tank heaters: 8–12 years thanks to our hard water. Tankless: 15–20 years with annual descaling." },
      { q: "Tank or tankless — which is better for Phoenix?", a: "Tankless wins on lifespan, energy bills, and endless hot water but costs more upfront. Tank is cheaper and faster to install." },
      { q: "Can you install same-day?", a: "Most standard tank-for-tank replacements are completed the same day you call. Tankless conversions usually schedule out a few days." },
      { q: "Should I flush my tank to extend its life?", a: "Yes — once a year in Phoenix. Sediment from our hard water is the #1 thing that kills tanks early." },
      { q: "Will you haul off the old heater?", a: "Always included in the flat price, along with the new drain pan and any code-required expansion tank." },
    ],
  },
  {
    slug: "drain-cleaning-and-clog-removal",
    name: "Drain Cleaning & Clog Removal",
    short: "From kitchen sinks to main lines — snaking, hydro-jetting, camera inspection.",
    title: "Drain Cleaning Phoenix | Plumbing & Drain Service Experts",
    description:
      "Phoenix plumbing and drain service from one licensed team: drain cleaning, clog removal, hydro jetting & sewer camera inspections. Same-day — 602-834-1208.",
    icon: "Waves",
    intro:
      "A clogged drain in Phoenix is rarely just hair — our hard water leaves mineral scale that traps grease and roots. We clear any drain in the house with the right tool, from a hand snake on a bathroom sink to high-pressure hydro-jetting on a main sewer line.",
    intro2:
      "Before we recommend hydro-jetting, we put a camera down the line so you can see exactly what's blocking it. That's how we know whether you've got grease, scale, roots, or a collapsed pipe — and it's the only honest way to pick the right tool.",
    whatWeFix: [
      "Kitchen sink, bathroom sink, tub, shower clogs",
      "Toilet clogs that snakes can't reach",
      "Main line backups and root intrusion",
      "Hydro-jetting for grease & scale",
      "Camera inspection of sewer lines",
    ],
    priceRange: "Single drain $129–$249 • Main line clearing $250–$550 • Hydro-jet $450–$900",
    warningSigns: [
      { t: "Multiple drains slow at the same time", d: "Single-drain clogs are local. Two or more at once means the main line is restricting." },
      { t: "Gurgling from a toilet when the tub drains", d: "Air pulling through a partial main-line block. Snake or jet decides on camera." },
      { t: "Water backing up at the lowest fixture", d: "First sign of a full main backup. Stop running washers and dishwashers until cleared." },
      { t: "Repeat clogs in the same drain", d: "Scale or root intrusion that a snake punches through but doesn't remove. Hydro-jet usually solves it." },
    ],
    pricingNote: "We price by the drain and by the tool, not by the hour. The camera inspection is built into main-line work so you see why we're choosing snake or jet.",
    pricingBullets: [
      "Single-drain flat rate — same price whether it takes 15 minutes or an hour",
      "Camera inspection included on main-line clears",
      "30-day warranty on cleared drains (snaked) and 90-day on hydro-jet",
    ],
    processNote: "Single bathroom or kitchen drains usually clear in under an hour. Main lines take longer when we camera first, but the trade-off is you don't pay for a hydro-jet you didn't need.",
    processSteps: defaultProcess,
    faqs: [
      { q: "Is hydro-jetting safe for old pipes?", a: "Yes — we camera the line first and adjust pressure for cast iron, clay, or older galvanized pipes." },
      { q: "Will a chemical drain cleaner do the same job?", a: "No. Liquid drain cleaners eat pipes and rarely clear the full blockage. They void many warranties." },
      { q: "Do you guarantee the drain stays clear?", a: "30 days on standard snaking, 90 days on hydro-jet. Camera footage shows what we cleared." },
      { q: "How often should a main line be jetted?", a: "Every 12–24 months for homes with mature trees or recurring grease buildup. We'll tell you on the camera footage if you're due." },
      { q: "How much does drain cleaning cost in Phoenix?", a: "Our typical Phoenix pricing is $129 to $249 for a single drain, $250 to $550 to clear a main line and $450 to $900 for hydro-jetting. We give you a flat price before any work starts, and the camera inspection is included on main-line clears." },
      { q: "Why does my kitchen drain clog again a few months after it was snaked?", a: "A snake opens a path through the clog, but it leaves the grease and mineral scale stuck to the pipe walls. New grease catches on that buildup and the drain slows down again. If it keeps happening, we camera the line and usually recommend hydro-jetting to clean the pipe back to bare wall." },
    ],
  },
  {
    slug: "plumbing-installation-phoenix",
    name: "Plumbing Installation",
    short: "Code-compliant installs of fixtures, pipes, and appliances.",
    title: "Plumbing Installation Phoenix AZ | Fixtures & Water Heaters",
    description:
      "Professional plumbing installation across the Phoenix Valley: fixtures, water heaters, water softeners, gas lines & new construction. 602-834-1208.",
    icon: "Wrench",
    intro:
      "From a single faucet to a whole-home repipe, every installation is permitted, inspected, and built to current Phoenix building code.",
    intro2:
      "We install homeowner-supplied fixtures as readily as our own — and warranty our labor either way. For bigger jobs (repipes, appliance hookups, gas runs) we pull the permit and meet the inspector so the work is on record for your home's value.",
    whatWeFix: [
      "Faucets, toilets, sinks, disposals",
      "Water heaters & softeners",
      "Pot fillers, ice maker lines, hose bibs",
      "Full repipes — PEX or copper",
      "Gas appliance hookups",
    ],
    priceRange: "Single fixture installs $175–$450 • Repipes from $4,500",
    warningSigns: null,
    pricingNote: "Installs are priced flat per fixture or per project. Permit fees, code upgrades, and any rough-in changes are itemized so you see exactly what you're paying for.",
    pricingBullets: [
      "Flat per-fixture or per-project pricing",
      "Permit fees itemized separately when required",
      "Financing available on installs over $1,000",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "Do you pull permits?", a: "Yes — when code requires one, we pull and clear the permit so the work is on record for your home's value." },
      { q: "Can I supply my own fixtures?", a: "Yes. Labor is warrantied either way; fixture warranty stays with the manufacturer." },
      { q: "How long does a typical fixture install take?", a: "Most single fixture installs are under two hours on site once the parts are staged." },
    ],
  },
  {
    slug: "leak-detection-and-repair",
    name: "Leak Detection & Repair",
    short: "Thermal imaging, acoustic, and slab-leak specialists.",
    title: "Leak Detection Phoenix | Same-Day Slab Leak Specialists",
    description:
      "Thermal imaging & acoustic leak detection built for Phoenix slab homes. Upfront quotes before work begins. Phoenix Plumbing Co. — 602-834-1208.",
    icon: "Search",
    intro:
      "A hidden leak in a Phoenix slab home can waste 10,000+ gallons a month before you ever see a stain. We use thermal imaging, acoustic listening devices, and pressure testing to locate the leak in hours, not days — then repair without tearing up your home.",
    intro2:
      "When a leak is under the slab we re-route through the attic or wall instead of jackhammering when the pipe geometry allows it. You decide between spot-repair and re-route once we show you the cost difference.",
    whatWeFix: [
      "Slab leaks under concrete foundations",
      "Pinhole leaks in copper",
      "Behind-wall leaks",
      "Yard / supply line leaks",
      "Pool plumbing leaks",
    ],
    priceRange: "Leak location $250–$450 • Slab leak repair $1,200–$3,500",
    warningSigns: [
      { t: "Warm spot on a tile or concrete floor", d: "Classic slab-leak signature in Phoenix — a hot-water line under the slab is leaking right under that spot." },
      { t: "Sound of running water with everything off", d: "Stand near the manifold or water heater in a silent house — a hiss or trickle means active flow." },
      { t: "Water bill jumps $40+ for no reason", d: "A constant pinhole or slab leak shows up on the bill long before it shows on the floor." },
      { t: "Mildew smell with no visible water", d: "Moisture wicking through drywall or insulation. Thermal imaging finds it without cutting." },
    ],
    pricingNote: "Leak location is a flat fee that's credited toward the repair if you choose to proceed with us. Slab repair pricing varies with depth, pipe material, and whether we spot-repair or re-route.",
    pricingBullets: [
      "Flat-rate leak location, credited toward repair",
      "Spot repair vs re-route both quoted so you can compare",
      "Concrete cutting and patch coordination handled in-house",
    ],
    processNote: "Locating a slab leak normally takes 1–2 hours on site. Repairs are scheduled separately so you have time to weigh the spot-repair vs re-route decision.",
    processSteps: defaultProcess,
    faqs: [
      { q: "How do I know if I have a slab leak?", a: "Warm spots on the floor, sudden spike in your water bill, sound of running water with everything off, or mildew smell are the four classic signs." },
      { q: "Can you find a slab leak without breaking concrete?", a: "Yes — thermal imaging and acoustic listening locate the leak first. Concrete only opens at the exact spot, if at all." },
      { q: "Re-route or spot repair?", a: "Spot repair is cheaper today; re-route through the attic prevents the next leak on the same line. We show you both prices and let you decide." },
      { q: "Does insurance cover slab leak repair?", a: "Many policies cover access (drywall, flooring, concrete) but not the pipe itself. We document the find for your adjuster." },
    ],
  },
  {
    slug: "commercial-plumbing-phoenix-az",
    name: "Commercial Plumbing",
    short: "Restaurants, offices, retail, multi-unit — fast response.",
    title: "Commercial Plumbing in Phoenix, AZ | Phoenix Plumbing Co.",
    description:
      "Restaurants, offices, retail, multi-unit — fast response. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "Building2",
    intro:
      "Downtime is expensive. We handle service, repairs, and scheduled maintenance for Phoenix restaurants, medical offices, retail, and multi-tenant buildings with after-hours and weekend dispatch.",
    intro2:
      "We work directly with facility managers and property owners — clean invoicing, COIs on file, and standing service-contract pricing for clients who want predictable monthly maintenance instead of break-fix calls.",
    whatWeFix: [
      "Grease trap service and repair",
      "Backflow testing & certification",
      "Commercial water heaters & boilers",
      "Sewer & drain line maintenance",
      "Tenant improvement plumbing",
    ],
    priceRange: "Service rate $145/hr • Quoted by project for larger work",
    warningSigns: [
      { t: "Slow floor drains in a kitchen", d: "Almost always grease load. Letting it ride risks a health-department write-up at the next inspection." },
      { t: "Repeat clogs at the same fixture", d: "Symptom of a deeper main-line issue. A camera pass settles it in one visit." },
      { t: "Failed annual backflow test letter", d: "City letters carry a fix-by date. We rebuild or replace assemblies and re-file the report." },
    ],
    pricingNote: "Commercial work is billed at a flat hourly rate during business hours with a project-based quote for anything over a half day. Service contracts lock in priority response and predictable monthly cost.",
    pricingBullets: [
      "Service-contract pricing for predictable monthly cost",
      "COIs and W-9 on file, ready for property managers",
      "After-hours and weekend dispatch available",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "Do you offer service contracts?", a: "Yes — quarterly and annual maintenance contracts with priority response are available." },
      { q: "Can you provide a COI for our building?", a: "Yes — we carry $2M general liability and can issue a certificate for any property manager or landlord on request." },
      { q: "Do you work after business hours?", a: "Yes. Restaurants and retail are typically scheduled after close so we don't interrupt operations." },
    ],
  },
  {
    slug: "kitchen-and-bathroom-plumbing",
    name: "Kitchen & Bathroom Plumbing",
    short: "Sinks, toilets, faucets, showers, tubs — installed & repaired.",
    title: "Kitchen & Bathroom Plumbing Phoenix AZ | Licensed Plumbers",
    description:
      "Sinks, toilets, faucets, showers, tubs — installed & repaired. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "ShowerHead",
    intro:
      "The kitchen and bath are where plumbing fails most. We repair and install every fixture in both rooms — and we clean up before we leave.",
    intro2:
      "We carry the most common cartridges, supply lines, and shutoffs on the truck so a leaking faucet or running toilet is usually fixed on the first visit instead of a parts-order callback.",
    whatWeFix: [
      "Faucets and sprayers",
      "Toilets, fill valves, flush systems",
      "Showers, tubs, valves, diverters",
      "Garbage disposals and air gaps",
      "P-traps, supply lines, shutoffs",
    ],
    priceRange: "Most installs $175–$450",
    warningSigns: [
      { t: "Toilet rocks or wobbles", d: "Wax ring is breaking down — left alone it stains the ceiling below." },
      { t: "Slow sink drain that wasn't slow last month", d: "P-trap buildup. Cheap to clear today, expensive when the trap corrodes through." },
      { t: "Constant trickle in a toilet bowl", d: "Failed flapper or fill valve. Adds $10–$30 a month to your bill until fixed." },
    ],
    pricingNote: "Most kitchen and bath repairs are flat-priced per fixture. We don't charge a separate trip fee on top of the repair price.",
    pricingBullets: [
      "Flat per-fixture pricing",
      "Common cartridges and parts stocked on the truck",
      "We clean up before we leave",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "Can you install a fixture I bought myself?", a: "Yes — we install homeowner-supplied fixtures with the same warranty on our labor." },
      { q: "Will you match my existing finish?", a: "Yes — we'll source matching trim or work with the finish you've chosen for your remodel." },
      { q: "How long does a typical faucet swap take?", a: "Most kitchen and bath faucets are out and back in under 90 minutes once we're on site." },
    ],
  },
  {
    slug: "plumbing-inspections-and-maintenance",
    name: "Plumbing Inspections & Maintenance",
    short: "Catch leaks early. 40-point inspection from $129.",
    title: "Plumbing Inspection & Maintenance Phoenix AZ | From $129",
    description:
      "Catch leaks early. 40-point inspection from $129. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Free quotes. Call 602-834-1208.",
    icon: "ClipboardCheck",
    intro:
      "An annual inspection catches the problems that cause $5,000 emergency calls. Our 40-point inspection checks every fixture, every shutoff, and your water heater, softener, and main line.",
    intro2:
      "You leave the visit with a written report — green / yellow / red on each component — and a flat price on anything that needs attention. No pressure to fix today; the report is yours to keep.",
    whatWeFix: [
      "Pre-purchase home inspections",
      "Annual maintenance checks",
      "Water heater flush & anode service",
      "Pressure & expansion tank testing",
    ],
    priceRange: "Inspection $129 • Maintenance plans from $19/mo",
    warningSigns: null,
    pricingNote: "Inspections are a flat $129 and the fee credits toward any repair you choose to schedule from the report. Maintenance plan members get the inspection at no charge.",
    pricingBullets: [
      "Flat $129 inspection — credited toward any repair you book",
      "Written green/yellow/red report you keep",
      "Maintenance plan members get the inspection free",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "How often should I inspect plumbing?", a: "Once a year for homes 10+ years old. Every two years for newer construction." },
      { q: "What does the inspection cover?", a: "Every visible supply line, drain, shutoff, fixture, the water heater, softener, pressure regulator, and the main line — 40 checkpoints total." },
      { q: "Do I have to fix what you find?", a: "No. The report is yours. Pricing on each fix is good for 30 days if you want to schedule later." },
    ],
  },
  {
    slug: "pipe-repair-and-replacement-phoenix-az",
    name: "Pipe Repair & Replacement",
    short: "Burst, leaking, or polybutylene pipes — repiped in days.",
    title: "Pipe Replacement Services Phoenix | Pipe Repair & Repiping",
    description:
      "Professional pipe replacement services in Phoenix: spot repairs, polybutylene replacement & whole-home repiping in PEX or copper. Licensed — 602-834-1208.",
    icon: "GitBranch",
    intro:
      "Phoenix homes built between 1978 and 1995 often have polybutylene supply pipe that fails without warning. We repair single leaks and full-home repipe in PEX or copper — most homes finished in 2–4 days.",
    intro2:
      "We protect floors and furniture before we cut, we cut as little drywall as possible, and we coordinate drywall patch so you're not left with open walls when we leave.",
    whatWeFix: [
      "Pinhole copper leaks",
      "Polybutylene replacement",
      "Galvanized steel replacement",
      "Slab pipe re-routes",
      "Hose bib and outdoor supply lines",
    ],
    priceRange: "Single repair $250–$750 • Full repipe $4,500–$12,000",
    warningSigns: [
      { t: "Bluish-green stains on copper fittings", d: "Early pinhole leak. Catching one usually means more are coming on the same run." },
      { t: "Repeat leaks in different rooms", d: "Polybutylene or aging galvanized failing systemwide. Repipe pays back faster than serial repairs." },
      { t: "Discolored water from one tap only", d: "Galvanized supply line corroding from the inside. Replacement, not repair." },
    ],
    pricingNote: "Spot repairs are flat-priced. Whole-home repipes are quoted per home after a walk-through — pipe material, story count, attic access, and drywall patch scope all factor in.",
    pricingBullets: [
      "Spot repair quoted flat before we start",
      "Whole-home repipe quoted after on-site walk-through",
      "Drywall patch coordination included",
    ],
    processNote: "Spot repairs are usually a single visit. Whole-home repipes typically run 2–4 days from cut-in to drywall patch coordination.",
    processSteps: defaultProcess,
    faqs: [
      { q: "Is PEX or copper better in Phoenix?", a: "PEX is more forgiving with our hard water and easier to install. Copper still wins on lifespan if water is softened." },
      { q: "How long does a whole-home repipe take?", a: "Most single-story homes finish in 2 days. Two-story or larger floor plans run 3–4 days." },
      { q: "Will you have to cut a lot of drywall?", a: "We cut as little as possible — typically 6–10 access holes for a whole-home repipe — and coordinate patch and paint." },
      { q: "How do I know if I need pipe repair or full pipe replacement?", a: "One pinhole leak in otherwise sound copper usually calls for a spot repair ($250–$750). Repeated leaks, polybutylene pipe, galvanized steel, or corroded fittings throughout the house are signs the system is failing as a whole — replacing it section by section costs more over time than a planned repipe. We camera-inspect and pressure-test first, then show you both numbers so you can decide." },
    ],
  },
  {
    slug: "gas-line-installation-and-repair-phoenix-az",
    name: "Gas Line Installation & Repair",
    short: "Licensed gas line installs, leak detection, code-compliant.",
    title: "Gas Line Installation & Repair Phoenix AZ | Licensed",
    description:
      "Licensed gas line installs, leak detection, code-compliant. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "Flame",
    intro:
      "Gas work is not DIY. Our licensed gas technicians install and repair gas lines for ranges, dryers, fire pits, pool heaters, and BBQs to current Phoenix code, with pressure-test documentation.",
    intro2:
      "Every gas line we touch gets a pressure test and a written sign-off so you have documentation for the city, for resale, and for your own records.",
    whatWeFix: [
      "New gas line runs",
      "Gas leak detection and repair",
      "Appliance hookups",
      "Outdoor lines for pools, fire pits, BBQs",
      "Code corrections from inspection failures",
    ],
    priceRange: "Most appliance hookups $350–$900 • Leak repair $250–$650",
    warningSigns: [
      { t: "Sulfur or rotten-egg smell anywhere", d: "Active gas leak. Leave the building, don't flip switches, call from outside." },
      { t: "Hissing near a gas appliance or meter", d: "Audible escape. Shut the appliance valve if you can reach it safely, then call." },
      { t: "Dead grass in a line directly above a gas run", d: "Underground gas line leak. Less urgent than indoor, but still needs same-week service." },
    ],
    pricingNote: "Appliance hookups are flat-priced by run length. New gas line runs and code-correction work are quoted per project after a walk-through.",
    pricingBullets: [
      "Flat-rate appliance hookups",
      "Permits pulled and pressure tests filed",
      "Written documentation handed to you on completion",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "I smell gas — what do I do?", a: "Leave the building immediately, do not flip switches, and call us from outside. We dispatch gas leaks first." },
      { q: "Do you pull permits for gas line work?", a: "Yes — every new gas run is permitted and pressure-tested. You get the documentation in writing." },
      { q: "Can you run a gas line for a new BBQ or fire pit?", a: "Yes — outdoor gas runs to BBQs, fire pits, and pool heaters are one of the most common projects we handle." },
    ],
  },
  {
    slug: "toilet-installation-and-repair-phoenix-az",
    name: "Toilet Repair & Installation",
    short: "Clogged, running, leaking, or due for replacement.",
    title: "Toilet Repair & Installation Phoenix | Phoenix Plumbing Co.",
    description:
      "Running, clogged, leaking or ready for replacement — Phoenix Plumbing Co. handles all toilet plumbing in Phoenix. Same-day repair & install. 602-834-1208.",
    icon: "Toilet",
    intro:
      "From a phantom flush to a wax-ring leak, we repair every brand of toilet. New install? We haul off the old one.",
    intro2:
      "We carry universal fill valves, flappers, and wax rings on every truck so a running or leaking toilet is fixed on the first visit. Full replacements include haul-away of the old toilet.",
    whatWeFix: [
      "Constantly running toilets",
      "Leaks at base or supply",
      "Weak / slow flush",
      "Full toilet replacement",
      "Smart and bidet toilet install",
    ],
    priceRange: "Repair $135–$285 • New install $250–$450 + toilet",
    warningSigns: [
      { t: "Phantom flushing every few hours", d: "Failed flapper letting water trickle through. Quiet fix, expensive bill if ignored." },
      { t: "Water at the base when you flush", d: "Wax ring failure. Pull-and-reseat fixes it before subfloor damage." },
      { t: "Toilet that rocks side to side", d: "Closet flange or bolts are loose. Five-minute tighten today, big repair next year." },
    ],
    pricingNote: "Repairs are flat-priced per issue. New installs are flat-priced including haul-away of the old toilet and a new wax ring and supply line.",
    pricingBullets: [
      "Flat-rate per repair — no hourly billing",
      "New install includes haul-away of old toilet",
      "Universal parts stocked on every truck",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "Should I replace or repair?", a: "If the bowl is cracked or the toilet is 20+ years old, replace. Otherwise repair is usually under $300." },
      { q: "Can you install a smart or bidet toilet?", a: "Yes — we install Toto, Kohler, and most smart-bidet brands, including the electrical outlet coordination if needed." },
      { q: "How long does a toilet swap take?", a: "Most replacements are done in 60–90 minutes including haul-away." },
    ],
  },
  {
    slug: "faucet-and-fixture-installation-phoenix-az",
    name: "Faucet & Fixture Installation",
    short: "Kitchen, bath, laundry — clean installs every time.",
    title: "Faucet Leak Repair & Fixture Installation | Phoenix, AZ",
    description:
      "Phoenix faucet leak repair and plumbing fixture replacement done right: faucets, sinks, tub & shower valves, disposals. Licensed & insured. 602-834-1208.",
    icon: "Droplet",
    intro:
      "Faucet replacements are quick — when they go right. When they don't, the wrong tool turns a $200 job into a flooded vanity. We bring the right tools.",
    intro2:
      "We install fixtures you've already purchased or source them for you. Labor warranty is the same either way; manufacturer warranty stays with the fixture brand.",
    whatWeFix: [
      "Kitchen and bar faucets",
      "Bathroom widespread & single-hole faucets",
      "Tub/shower valves and trim",
      "Outdoor hose bibs and frost-free faucets",
    ],
    priceRange: "Most installs $175–$350",
    warningSigns: null,
    pricingNote: "Faucet and fixture installs are flat-priced per fixture. Old shutoff valves that won't seal get replaced at cost — we'll show you the part before swapping it.",
    pricingBullets: [
      "Flat per-fixture install pricing",
      "Old shutoff valves replaced at-cost when needed",
      "Labor warrantied whether you supplied the fixture or we did",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "Can you match my existing finish?", a: "Yes — we'll source matching trim or work with the fixture you've purchased." },
      { q: "Will old shutoff valves slow the install?", a: "Sometimes — if they won't fully close we swap them at cost and show you the part. It adds 30–45 minutes." },
      { q: "Do you install tub and shower trim too?", a: "Yes, including diverter and pressure-balance cartridge swaps for most major brands." },
      { q: "Is it worth repairing a leaking faucet, or should I replace it?", a: "If the faucet body is sound, a cartridge or seal repair is usually the cheaper fix and takes under an hour. If the finish is pitted, the body is scaled inside, or parts are discontinued, replacement costs less over its life. We carry common cartridges and full replacement faucets on the truck, so either way it's typically one visit." },
    ],
  },
  {
    slug: "sewer-line-repair-and-replacement-phoenix-az",
    name: "Sewer Line Repair & Replacement",
    short: "Camera inspection and trenchless repair when possible.",
    title: "Sewer Line Repair & Replacement Phoenix AZ | Trenchless",
    description:
      "Camera inspection and trenchless repair when possible. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "PipeIcon",
    intro:
      "Phoenix's clay soil and mature mesquite roots are hard on sewer lines. We camera every line first, then choose trenchless pipe lining when possible to avoid tearing up your yard.",
    intro2:
      "You watch the camera footage with us before any decision is made. If the pipe is intact but cracked or root-invaded, trenchless lining usually wins. If it's collapsed or off-grade, traditional dig is the honest answer.",
    whatWeFix: [
      "Root-intrusion clearing",
      "Sewer camera inspections",
      "Trenchless pipe lining & bursting",
      "Full sewer replacement",
      "Cleanout installation",
    ],
    priceRange: "Camera inspection $250 • Trenchless lining $90–$250/ft • Replacement $5,500–$18,000",
    warningSigns: [
      { t: "Sewage smell in the yard", d: "Cracked or offset sewer line venting underground. Camera confirms it the same visit." },
      { t: "Patches of unusually green grass over the sewer path", d: "Sewage leaking out is fertilizing the lawn. Don't wait — pipe is already compromised." },
      { t: "Recurring main-line backups", d: "Roots growing back into the same joint. Lining or replacement is the long-term fix." },
    ],
    pricingNote: "Camera inspection is a flat $250 and credits toward any sewer repair you proceed with. Lining and replacement are quoted per foot after we see the footage with you.",
    pricingBullets: [
      "Flat $250 camera inspection — credits to any sewer repair",
      "Per-foot pricing on lining and replacement, shown after camera",
      "Cleanout install priced flat by site access",
    ],
    processNote: "Camera and quote happen in one visit. Lining is usually a one-day job; full dig-and-replace runs 2–5 days depending on length and depth.",
    processSteps: defaultProcess,
    faqs: [
      { q: "Trenchless vs dig — which is right?", a: "If the pipe is intact but cracked, trenchless lining wins. If the pipe is collapsed or off-grade, traditional dig is required." },
      { q: "Do you offer trenchless sewer repair?", a: "Yes — pipe lining and pipe bursting, when the existing line geometry supports it. Camera footage decides." },
      { q: "Will my yard be torn up?", a: "Trenchless usually means two small access pits. Full replacement is a trench the length of the line — we restore grade but landscaping is on you or your landscaper." },
      { q: "How long does a lined pipe last?", a: "Manufacturer-rated 50 years. The new liner is essentially a pipe-within-a-pipe." },
    ],
  },
  {
    slug: "water-filtration-and-softener-systems-phoenix-az",
    name: "Water Filtration & Softener Systems",
    short: "Whole-home softeners for Phoenix's brutal hard water.",
    title: "Water Softener & Filtration Systems Phoenix AZ | Installed",
    description:
      "Whole-home softeners for Phoenix's brutal hard water problems. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "Filter",
    intro:
      "Phoenix tap water averages 16–20 grains per gallon — extremely hard. A whole-home softener extends your water heater life by years and stops scale buildup on fixtures.",
    intro2:
      "Before we recommend a system, we test your water and your usage so the softener is sized correctly. Oversized systems waste salt; undersized systems cycle too often. We install the right size.",
    whatWeFix: [
      "Whole-home salt softeners",
      "Salt-free conditioners",
      "Reverse osmosis under-sink systems",
      "Sediment & carbon filtration",
    ],
    priceRange: "Softener install $1,800–$3,500 • RO from $650",
    warningSigns: null,
    pricingNote: "Water filtration and softener installs are quoted flat after a quick water test and a look at the install location. Salt-based and salt-free options shown side by side with cost-per-year comparison.",
    pricingBullets: [
      "Flat install price after water test and site walk-through",
      "Salt vs salt-free options compared side by side",
      "Financing available on whole-home systems",
    ],
    processNote: "Most whole-home softener installs take a half day on site. Under-sink RO systems are usually 2–3 hours. Both are scheduled jobs — not emergency calls.",
    processSteps: defaultProcess,
    faqs: [
      { q: "Salt or salt-free?", a: "Salt is the only true softener. Salt-free systems condition water but don't remove hardness minerals." },
      { q: "How long until I notice the difference?", a: "Within a week — softer skin, less scale on glass and fixtures, soap that actually lathers." },
      { q: "Will a softener help my water heater last longer?", a: "Yes — soft water is the single biggest factor in extending water heater life in Phoenix. Often adds 3–5 years to a tank's lifespan." },
    ],
  },
  {
    slug: "garbage-disposal-installation-and-repair-phoenix-az",
    name: "Garbage Disposal Repair & Install",
    short: "Jammed, leaking, dead? Same-day swap.",
    title: "Garbage Disposal Repair & Install Phoenix AZ | Same-Day",
    description:
      "Jammed, leaking, dead? Same-day swap. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Family-owned since 2012. Call 602-834-1208.",
    icon: "Recycle",
    intro:
      "Most disposals last 8–12 years. We repair what's worth repairing and replace what isn't — usually same-day.",
    intro2:
      "We stock 3/4 HP and 1 HP units on the truck (the right size for Phoenix homes with citrus rinds) so a dead disposal is usually swapped on the first visit.",
    whatWeFix: [
      "Jammed flywheels",
      "Leaks at sink flange or housing",
      "Dead motors",
      "Full replacement with new mounting",
    ],
    priceRange: "Repair $125–$225 • Replacement $250–$475 installed",
    warningSigns: [
      { t: "Humming but not spinning", d: "Jammed flywheel. A free reset attempt comes before any parts pricing." },
      { t: "Water under the cabinet", d: "Cracked housing or failed sink flange. Almost always a replacement, not a repair." },
      { t: "Disposal is 8+ years old and slowing down", d: "Motor wearing out. Replacement is usually cheaper than chasing repeat repair calls." },
    ],
    pricingNote: "Repairs are flat-priced. Replacements are flat-priced including new mounting hardware and haul-away of the old unit.",
    pricingBullets: [
      "Flat repair and replacement pricing",
      "3/4 HP and 1 HP units stocked on the truck",
      "Old unit hauled away — included",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "Do I need a specific brand?", a: "We recommend 3/4 HP or higher for Phoenix homes — handles citrus rinds and avoids burnouts." },
      { q: "Can you reuse my old mounting?", a: "Sometimes — but we recommend new mounting hardware so the warranty stays clean." },
      { q: "What shouldn't go in a disposal?", a: "Coffee grounds, fibrous vegetables (celery, artichokes), bones, and grease. Everything else is fair game on a 3/4 HP unit." },
    ],
  },
  {
    slug: "bathroom-remodeling-plumbing-phoenix-az",
    name: "Bathroom Remodeling Plumbing",
    short: "Rough-ins, fixture relocates, finish work for your remodel.",
    title: "Bathroom Remodel Plumbing Phoenix AZ | Phoenix Plumbing Co.",
    description:
      "Rough-ins, fixture relocates, finish work for your remodel. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "Bath",
    intro:
      "We partner with Phoenix homeowners and remodelers on bathroom projects of every size — from a vanity swap to moving a tub and shower across the room.",
    intro2:
      "Bathroom remodels are scheduled projects, not same-day calls. We coordinate with your GC or designer, sequence rough-in around tile and drywall, and return for finish plumbing once the trades are out of the way.",
    whatWeFix: [
      "New bath rough-in",
      "Drain and supply relocates",
      "Curbless shower & wet room plumbing",
      "Finish plumbing & fixture sets",
    ],
    priceRange: "Vanity swap $450–$850 • Full bath rough-in $3,500–$8,500",
    warningSigns: null,
    pricingNote: "Remodel work is quoted per project after a walk-through and a look at the plans. Vanity swaps and finish-only jobs are quoted flat; full bath rough-ins price by fixture count, relocation distance, and slab vs above-slab.",
    pricingBullets: [
      "Per-project quote after walk-through and plans review",
      "Coordinated with your GC, designer, or other trades",
      "Code-compliant rough-in with permits pulled when required",
    ],
    processNote: "Remodels typically have two plumbing visits: rough-in (before drywall) and finish (after tile and paint). We schedule both at the start so your project doesn't stall.",
    processSteps: defaultProcess,
    faqs: [
      { q: "Do you work with my contractor?", a: "Yes — we sub plumbing for GCs and design-build firms across the Valley." },
      { q: "Can you move a shower or tub to a different wall?", a: "Yes — drain and supply relocates are routine on remodels. Slab cutting may be required and is itemized in the quote." },
      { q: "Do you pull the plumbing permit?", a: "Yes — we pull the plumbing permit and meet the inspector at rough-in and final." },
    ],
  },
  {
    slug: "kitchen-remodeling-plumbing-phoenix-az",
    name: "Kitchen Remodeling Plumbing",
    short: "Sink relocations, gas hookups, pot fillers, ice makers.",
    title: "Kitchen Remodel Plumbing Phoenix AZ | Phoenix Plumbing Co.",
    description:
      "Sink relocations, gas hookups, pot fillers, ice makers. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "ChefHat",
    intro:
      "Kitchen remodels live and die by plumbing layout. We rough in for islands, pot fillers, beverage stations, and gas ranges.",
    intro2:
      "We come in early to confirm vent and drain geometry before cabinets are ordered — catching an island-vent issue in the design phase is the difference between a clean remodel and a stalled one.",
    whatWeFix: [
      "Sink and dishwasher relocates",
      "Pot fillers & ice maker lines",
      "Gas range hookups",
      "Disposal & instant hot water installs",
    ],
    priceRange: "Most projects $2,500–$7,000",
    warningSigns: null,
    pricingNote: "Kitchen remodel plumbing is quoted per project. We split the bid between rough-in (before drywall and cabinets) and finish (after countertops are in) so you see exactly what each phase costs.",
    pricingBullets: [
      "Per-project quote split between rough-in and finish phases",
      "Design-phase consult on island vent and layout",
      "Coordinated with your cabinet and counter installers",
    ],
    processNote: "Most kitchen remodels have two plumbing visits: a rough-in before drywall/cabinets, and finish after countertops are set. We sync to your overall schedule.",
    processSteps: defaultProcess,
    faqs: [
      { q: "Do you handle island sink drains?", a: "Yes — we install code-compliant island vent solutions (loop or AAV per local code)." },
      { q: "Can you run a new gas line for a pro range?", a: "Yes — pro ranges typically need a larger gas line. We size the run, pull the permit, and pressure-test." },
      { q: "Do I need a pot filler line if I'm just thinking about it?", a: "Rough it in during the remodel even if you skip the fixture today — cutting drywall later costs more than the pipe." },
    ],
  },
  {
    slug: "backflow-prevention-and-testing-phoenix-az",
    name: "Backflow Prevention & Testing",
    short: "Certified annual backflow testing and assembly installs.",
    title: "Backflow Testing & Prevention Phoenix AZ | Certified",
    description:
      "Certified annual backflow testing and assembly installs. Phoenix Plumbing Co. Upfront flat-rate quotes. Written workmanship warranty. Call 602-834-1208.",
    icon: "ShieldCheck",
    intro:
      "Phoenix and most Valley cities require annual backflow testing on irrigation and commercial assemblies. We are certified testers and we install RPZ, double-check, and PVB assemblies.",
    intro2:
      "We file the test report directly with the city so you don't have to chase paperwork. If your assembly fails, we rebuild or replace it the same week so you stay compliant.",
    whatWeFix: [
      "Annual backflow testing (reports filed)",
      "RPZ, DCV, PVB installs",
      "Failed-test repair & rebuild kits",
    ],
    priceRange: "Annual test $65–$95 • New assembly install from $650",
    warningSigns: null,
    pricingNote: "Annual tests are a flat $65–$95 depending on assembly type. New installs and failed-test rebuilds are flat-priced per assembly.",
    pricingBullets: [
      "Flat-rate annual test, report filed with the city",
      "Failed-test rebuilds priced flat per assembly",
      "New RPZ/DCV/PVB installs quoted before any work starts",
    ],
    processSteps: defaultProcess,
    faqs: [
      { q: "Do you file the report with the city?", a: "Yes — we file directly with the City of Phoenix and most surrounding municipalities." },
      { q: "What happens if my assembly fails?", a: "We rebuild or replace it the same week and retest so your compliance window doesn't lapse." },
      { q: "How often does my assembly need testing?", a: "Annually for most Valley cities. The water department sends a reminder letter; we can file on receipt." },
    ],
  },
  {
    slug: "new-construction-plumbing-phoenix-az",
    name: "New Construction Plumbing",
    short: "Rough-ins, finish, and gas for builders and GCs.",
    title: "Phoenix Plumbing Contractors | New Construction, Remodels & Repairs",
    description:
      "Licensed plumbing contractors in Phoenix for new builds, remodels & repairs. Bonded & insured, free quotes, fast scheduling. Call 602-834-1208.",
    icon: "Hammer",
    intro:
      "Custom homes and small infill projects. We bid by plan, hit your schedule, and stand behind the work for the long haul.",
    intro2:
      "We bid from plans, hold the bid through your schedule, and show up when the schedule calls for us — underground, top-out, and trim. References from active builders on request.",
    whatWeFix: [
      "Underground / slab rough-in",
      "Wall and ceiling rough-in",
      "Finish plumbing & trim out",
      "Gas distribution",
    ],
    priceRange: "Quoted per plan",
    warningSigns: null,
    pricingNote: "New construction is quoted per plan after a takeoff. Bids are held through your schedule with line-item change orders if scope shifts.",
    pricingBullets: [
      "Bid per plan after a full takeoff",
      "Line-item change orders if scope shifts mid-project",
      "Three phases priced separately: underground, top-out, trim",
    ],
    processNote: "New construction has three plumbing phases: underground (before slab), top-out (after framing, before drywall), and trim (after paint). We schedule each to your overall project calendar.",
    processSteps: defaultProcess,
    faqs: [
      { q: "Will you bid from plans?", a: "Yes — submit plans and we'll return a per-plan bid within a week." },
      { q: "Do you carry the GL coverage we need?", a: "Yes — $2M general liability with COI issued to your GC on request." },
      { q: "Can you handle production builds as well as customs?", a: "We focus on customs and small infill rather than large production tracts — that's where our model fits best." },
    ],
  },
  {
    slug: "slab-leak-detection-and-repair-phoenix-az",
    name: "Slab Leak Detection & Repair",
    short: "Warm floors, high water bills, hidden slab leaks — found and fixed.",
    title: "Slab Leak Detection & Repair Phoenix | Same-Day Service",
    description:
      "Slab leak detection and repair across the Phoenix Valley. Electronic leak location, honest repair options, insurance help. Call 602-834-1208.",
    icon: "Search",
    intro:
      "Slab leaks are one of the most common — and most expensive — plumbing failures in Phoenix. Hard water eats pinholes in the copper supply lines running under your concrete foundation, and once one leaks, the next is usually not far behind. We locate slab leaks with acoustic listening, thermal imaging, and pressure testing so the repair happens over the pipe, not across your living room.",
    intro2:
      "You get two written quotes on the same visit — spot repair or reroute — and we tell you which one is honest for your house. Most Valley homes see a licensed plumber the same day.",
    whatWeFix: [
      "Hot-water slab leaks (warm-spot leaks)",
      "Cold-water slab leaks under bathrooms and kitchens",
      "Pinhole leaks in copper supply lines",
      "Slab leak reroutes through attic or wall",
      "Spot slab repair with concrete cut and patch",
      "Whole-home repipe when the copper is done",
    ],
    priceRange: "Location $250–$450 • Spot repair $1,200–$2,500 • Reroute $2,000–$4,500",
    warningSigns: [
      { t: "Warm spot on a tile or concrete floor", d: "The single most reliable slab-leak signature — a hot-water line under the slab is leaking right below that spot." },
      { t: "Water bill jumps $40+ with no change in usage", d: "A constant slab leak shows up on the bill weeks before you see a stain." },
      { t: "Sound of running water with everything off", d: "In a silent house, stand near the water heater. A hiss or trickle means active flow." },
      { t: "Damp baseboards or mildew smell", d: "Moisture wicking up from the slab into drywall. Thermal imaging locates it without cutting." },
      { t: "Water heater running constantly", d: "A leaking hot line keeps the heater cycling. Gas or electric bill climbs alongside the water bill." },
    ],
    pricingNote: "Leak location is a flat fee that credits toward the repair if you proceed with us. Spot vs. reroute pricing depends on depth, pipe material, and access — both options are quoted before any cutting begins.",
    pricingBullets: [
      "Flat-rate location, credited toward repair",
      "Spot repair and reroute both priced side by side",
      "Concrete cutting and drywall patch coordinated in-house",
      "Written workmanship warranty on every repair",
    ],
    processNote: "Locating a slab leak normally takes 1–2 hours on site. Repairs are scheduled separately so you can weigh spot vs. reroute against your budget and long-term plans.",
    processSteps: defaultProcess,
    faqs: [
      { q: "How do I know if I have a slab leak?", a: "The four classic signs are a warm spot on the floor, a water bill spike with no usage change, the sound of running water when everything is off, and a mildew smell with no visible source. Do the meter test first — with every fixture shut off, if the meter's small dial keeps turning, water is going somewhere it should not." },
      { q: "Does homeowners insurance cover slab leak repair?", a: "Many Arizona policies cover the water damage and the cost to access the leak (drywall, flooring, concrete cutting), but not the pipe repair itself. We document the find with photos and a written report for your adjuster." },
      { q: "How long does slab leak detection take?", a: "Most locations take 1–2 hours on site. We isolate the hot and cold loops, listen acoustically, and confirm with thermal imaging so we know exactly where to open before anything cuts concrete." },
      { q: "Do you have to break my floor to fix it?", a: "Not always. A reroute abandons the leaking line and runs a new one through the attic or wall — no jackhammer. When the pipe geometry does not allow a reroute, a small window in the slab (typically 12–18 inches) fixes the leak and gets patched the same day." },
      { q: "How much does slab leak repair cost in Phoenix?", a: "Spot repairs typically run $1,200–$2,500 and reroutes run $2,000–$4,500. If the copper has already leaked in multiple locations, a full repipe ($4,500–$12,000) usually costs less over 3 years than serial spot repairs." },
    ],
  },
  {
    slug: "phoenix-plumbing-contractors",
    name: "Phoenix Plumbing Contractors",
    short: "Licensed & ROC-certified plumbing contractors for homeowners, builders, and GCs.",
    title: "Phoenix Plumbing Contractor for Builders & GCs | Licensed",
    description:
      "Licensed plumbing contractor in Phoenix for builders, GCs & property managers. New construction, remodels & service work. Call 602-834-1208.",
    h1: "Plumbing Contractor for Phoenix Builders, GCs & Property Managers",
    icon: "Hammer",
    intro:
      "Phoenix Plumbing Co. is a licensed AZ ROC plumbing contractor serving Valley homeowners, builders, general contractors, and property managers. We handle work most break-fix shops walk away from — permitted installations, code-compliance corrections, tenant improvements, multi-unit maintenance, and full new construction rough-ins — bonded and insured, with COIs available for GCs on request.",
    intro2:
      "Because we run residential service and commercial contracting under one roof, we can bid a single custom home, a small infill project, or a tenant improvement without subbing anything out. Same crew, same warranty, same phone number.",
    whatWeFix: [
      "New construction rough-in and finish",
      "Commercial tenant improvements",
      "Bathroom and kitchen remodel rough-in",
      "Backflow assembly installs and annual testing",
      "Gas line runs and pressure-tested appliance hookups",
      "Permitted repipe and repair work with inspection",
    ],
    priceRange: "Bid per project after plan review or on-site walk-through",
    warningSigns: null,
    pricingNote: "Contractor work is quoted per project after a takeoff or on-site walk-through. Bids are held through your build schedule, with line-item change orders if scope shifts.",
    pricingBullets: [
      "Written fixed-fee bid held through the schedule",
      "COI and W-9 available for GCs and property managers",
      "AZ ROC licensed, bonded, and insured",
      "Permits pulled and inspections met at each phase",
    ],
    processNote: "Contractor projects have three plumbing phases: underground / rough-in, top-out, and trim. We schedule each around your framing, drywall, tile, and paint sequence so nothing gets torn back out.",
    processSteps: defaultProcess,
    faqs: [
      { q: "Are you a licensed AZ ROC plumbing contractor?", a: "Yes — Phoenix Plumbing Co. is fully AZ ROC licensed, bonded, and insured. We carry $2M general liability and can issue COIs to your GC or property manager on request." },
      { q: "Do you work with general contractors and builders?", a: "Yes. We sub plumbing for GCs, design-build firms, and custom-home builders across the Valley. We bid from plans, hold the price through the build, and meet the inspector at each phase." },
      { q: "Can you handle commercial tenant improvements?", a: "Yes — retail, medical, restaurants, and multi-tenant TIs are a regular part of our workload. We pull the plumbing permit, coordinate with your GC, and hand you signed inspection cards at final." },
      { q: "Why hire a licensed plumbing contractor instead of a handyman?", a: "In Arizona, any plumbing work over $1,000 legally requires an ROC-licensed contractor. Licensed contractors carry bonding and general liability, can pull permits, and give you real recourse through the ROC if something goes wrong. An unlicensed installer offers none of that." },
      { q: "Do you pull permits and meet inspectors?", a: "Yes. We pull the plumbing permit, coordinate the inspection window, and meet the inspector for underground, top-out, and trim on every permitted job." },
      { q: "What's the difference between a plumber and a plumbing contractor?", a: "Any licensed plumber can make repairs. A plumbing contractor is licensed with the Arizona ROC to take legal responsibility for a project — pulling permits, meeting code, passing inspection, and warrantying the work. For new construction, remodels, or commercial work in Phoenix, you want a licensed plumbing contractor of record. Phoenix Plumbing Co. is AZ ROC licensed, bonded, and insured." },
    ],
  },
];

export const SERVICE_BY_SLUG: Record<string, Service> = Object.fromEntries(
  SERVICES.map((s) => [s.slug, s])
);
